import {
    DiscrepancyReportComplianceGroup,
    JobCardComplianceGroup,
} from '@/features/LineItems/types';
import {
    ADStatusReportResponse,
    ADStatusRowWithContext,
    AviationDocumentCategory,
    CategoryComplianceStats,
    ComplianceCheckResult,
    ProjectDocComplianceFailure,
    Component,
    ComponentItem,
    ComponentSort,
    ComponentStatus,
    ComponentWithInstalledItem,
    FileUploadProgress,
    MaintenanceActivityRow,
    MaintenancePlan,
    MaintenanceTask,
    ProjectMemberCreate,
    ProjectType,
    SBStatusReportResponse,
    StatusReportAIInsight,
} from '@/features/end-of-lease/types';
import { DocType, LeasingDocs } from '@/models/docs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { isNil, omitBy } from 'lodash';

import { toCamelCase, toSnakeCase } from '@/lib/case';
import { ComponentDoc } from '@/lib/types';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import type { ADStatusReportSummaryStats } from '@/features/AssetDetailView/FolderDetailView/C006AirframeADs/types';
import type {
    AdsrChangeSummary,
    AdsrUploadOutcome,
} from '@/features/AssetDetailView/FolderDetailView/B004CertifiedStatusAirframeApplianceADs/types';

import { Activity, AviationDocumentType, DocumentStatus, LeasingAircraft, LeasingProject, ProjectTask, ProjectTaskCreate, ProjectTaskUpdate, DocumentStatsResponse, ReportSummary } from './types';

type Pagination = {
    limit: number;
    skip: number;
};

type ProjectId = { projectId: number };

type PaginatedResponse<t> = {
    total: number;
    results: t[];
    totalCount?: number;
};

export async function fetchAllPaginatedItems<T>(
    fetchPageFn: (pg: Pagination) => Promise<PaginatedResponse<T>>,
) {
    const allFetchedItems: T[] = [];
    const limit = 100;
    let skip = 0;
    let hasMore = true;

    while (hasMore) {
        const response = await fetchPageFn({ limit, skip });

        allFetchedItems.push(...response.results);

        hasMore = allFetchedItems.length < response.total;
        skip += limit;
    }

    return allFetchedItems;
}

export const listDocuments = async ({
    client,
    projectId,
    category,
    type,
    fileIds,
    limit,
    skip,
    matched,
}: Pagination &
    ProjectId & {
        client: AxiosInstance;
        fileIds?: number[];
        category?: AviationDocumentCategory;
        type?: AviationDocumentType;
        matched?: boolean;
    }) => {
    let res = await client.get(`/api/leasing/`, {
        params: toSnakeCase({
            projectId,
            fileIds,
            type,
            category,
            matched,
            limit,
            skip,
        }),
        paramsSerializer: { indexes: null },
    });
    return toCamelCase<PaginatedResponse<LeasingDocs>>(res.data);
};

export const reingestDocument = async ({
    client,
    docId,
}: {
    client: AxiosInstance;
    docId: number;
}) => {
    await client.post(`/api/leasing/documents/${docId}/reingest`);
};

export const updateDocumentType = async ({
    client,
    docId,
    newType,
}: {
    client: AxiosInstance;
    docId: number;
    newType: DocType;
}) => {
    await client.post(`/api/leasing/documents/${docId}/update_type`, {
        new_type: newType,
    });
};

export const useDocuments = ({
    projectId,
    category,
    type,
    fileIds,
    limit,
    skip,
    matched,
}: Pagination &
    ProjectId & {
        fileIds?: number[];
        category?: AviationDocumentCategory;
        type?: AviationDocumentType;
        matched?: boolean;
    }) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            'leasingDocuments',
            { projectId, limit, skip, fileIds, matched },
        ],
        queryFn: async () =>
            listDocuments({
                client,
                projectId,
                category,
                type,
                fileIds,
                limit,
                skip,
                matched,
            }),
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
    });
};

// POST /api/leasing/
export interface ProjectCreate {
    aircraft?: LeasingAircraft;
    title: string;
    msn?: string | null;
    aircraftType?: string | null;
    endOfLeaseDate?: string | null;
    deliveryDate?: string | null;
    lessee?: string | null;
    aircraftId?: number | null;
    members?: ProjectMemberCreate[];
    projectType?: string;
    lessor?: string | null;
    leaseStartDate?: string | null;
}

// GET /api/leasing/projects
export const useProjects = ({
    projectType,
    enabled: queryEnabled = true,
}: {
    projectType?: ProjectType | null;
    enabled?: boolean;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingProjects', projectType],
        queryFn: async () => {
            const response = await client.get(`/api/leasing/projects`, {
                params: { project_type: projectType },
            });
            return toCamelCase<LeasingProject[]>(response.data);
        },
        enabled: enabled && queryEnabled,
        refetchOnWindowFocus: true,
    });
};

// GET /api/leasing/projects/{id}
export const useProject = (id: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingProjects', id],
        queryFn: async () => {
            if (!id) return;
            const response = await client.get(`/api/leasing/projects/${id}`);
            return toCamelCase<LeasingProject>(response.data);
        },
        enabled: enabled && id !== null,
        refetchOnWindowFocus: true,
    });
};

// DELETE /api/leasing/projects/{id}
// POST /api/leasing/projects
export const useCreateProject = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (project: ProjectCreate) => {
            const response = await client.post(
                `/api/leasing/projects`,
                toSnakeCase(project),
            );
            return toCamelCase<LeasingProject>(response.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['leasingProjects'] }),
    });
};

// POST /api/leasing/projects/{project_id}/tasks
export const useCreateProjectTask = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (task: ProjectTaskCreate) => {
            const response = await client.post(
                `/api/leasing/projects/${projectId}/tasks`,
                toSnakeCase(task),
            );
            return toCamelCase<ProjectTask>(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leasingProjects', projectId],
            });
        },
    });
};

// PATCH /api/leasing/projects/{project_id}/tasks/{task_id}
export const useUpdateProjectTask = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            taskId,
            data,
        }: {
            taskId: number;
            data: ProjectTaskUpdate;
        }) => {
            const response = await client.patch(
                `/api/leasing/projects/${projectId}/tasks/${taskId}`,
                toSnakeCase(data),
            );
            return toCamelCase<ProjectTask>(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leasingProjects', projectId],
            });
        },
    });
};

// GET /api/leasing/preview/{id}
export const usePreview = (id?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['preview', id],
        queryFn: async () => {
            let res = await client.get(`/api/leasing/preview/${id}`);
            return res.data as string;
        },
        enabled: enabled && !!id,
    });
};

// POST /api/leasing/projects/{id}/status
// PATCH /api/leasing/projects/{id}
export const useUpdateProject = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            projectId,
            title,
        }: {
            projectId: number;
            title: string;
        }) => {
            const response = await client.patch(
                `/api/leasing/projects/${projectId}`,
                { title },
            );
            return toCamelCase<LeasingProject>(response.data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['leasingProjects'] });
            queryClient.invalidateQueries({
                queryKey: ['leasingProjects', variables.projectId],
            });
        },
    });
};

// GET /api/leasing/projects/{id}/document_stats
export const useDocumentStats = (
    projectId: number,
    params?: { folder?: string; subfolder?: string },
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documentStats', projectId, params?.folder, params?.subfolder],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/projects/${projectId}/document_stats`,
                { params: omitBy(params ?? {}, isNil) },
            );
            return toCamelCase<DocumentStatsResponse>(response.data);
        },
        enabled: enabled && !!projectId,
    });
};

// GET /api/leasing/projects/{id}/documents
export const useDocumentsByProject = (id: number, reference?: string) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documentsByProject', id, reference],
        queryFn: async () => {
            let res = await client.get(
                `/api/leasing/projects/${id}/documents`,
                {
                    params: { reference },
                },
            );
            return toCamelCase<PaginatedResponse<LeasingDocs>>(res.data);
        },
        enabled,
    });
};

// GET /api/leasing/projects/{project_id}/ad-status-rows
export const useADStatusRows = (projectId: number) => {
    const { client, enabled } = useAuthAxios();
    const isValidProjectId = Number.isFinite(projectId) && projectId > 0;

    return useQuery({
        queryKey: ['adStatusRows', projectId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/ad-status-rows`,
            );
            return toCamelCase<ADStatusReportResponse>(res.data);
        },
        enabled: enabled && isValidProjectId,
    });
};

// POST /api/leasing/projects/{project_id}/ad-status-reports/highlights/dismiss
// Dismisses the change-highlights for the project's active ADSR version (shared,
// per-version, persisted). On success re-reads the rows so the banner + tints
// clear from the server's `highlightsActive`.
export const useDismissHighlights = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.post(
                `/api/leasing/projects/${projectId}/ad-status-reports/highlights/dismiss`,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['adStatusRows', projectId],
            });
        },
    });
};

// GET /api/leasing/projects/{project_id}/ad-status-reports/change-summary
// Returns the before/after totals for a project's surfaced pending ADSR update,
// or null when there is nothing to reconcile. `pendingDocId` isn't a request
// param (the endpoint serves whichever pending is surfaced) — it's part of the
// cache key so a *replacement* pending on the same project fetches fresh counts
// instead of reusing the previous pending's cached summary.
export const useADSRChangeSummary = (
    projectId: number,
    isEnabled = true,
    pendingDocId?: number | null,
) => {
    const { client, enabled } = useAuthAxios();
    const isValidProjectId = Number.isFinite(projectId) && projectId > 0;

    return useQuery({
        queryKey:
            pendingDocId == null
                ? ['adsrChangeSummary', projectId]
                : ['adsrChangeSummary', projectId, pendingDocId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/ad-status-reports/change-summary`,
            );
            return res.data == null
                ? null
                : toCamelCase<AdsrChangeSummary>(res.data);
        },
        enabled: enabled && isValidProjectId && isEnabled,
    });
};

// GET .../ad-status-reports/upload-outcome
// Returns the surfaced pending's `verdict` (older / duplicate / msn_mismatch, or
// null for a newer pending) + status dates, or null when there's nothing to act
// on — the signal the B004 upload-outcome dialog router picks its dialog from.
export const useADSRUploadOutcome = (
    projectId: number,
    isEnabled = true,
    // Poll while the update flow waits for reconcile to surface the pending; default off.
    refetchInterval: number | false = false,
) => {
    const { client, enabled } = useAuthAxios();
    const isValidProjectId = Number.isFinite(projectId) && projectId > 0;

    return useQuery({
        queryKey: ['adsrUploadOutcome', projectId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/ad-status-reports/upload-outcome`,
            );
            return res.data == null
                ? null
                : toCamelCase<AdsrUploadOutcome>(res.data);
        },
        enabled: enabled && isValidProjectId && isEnabled,
        refetchInterval,
    });
};

// POST .../ad-status-reports/pending/accept — apply the pending update.
export const useAcceptADSRUpdate = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.post(
                `/api/leasing/projects/${projectId}/ad-status-reports/pending/accept`,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['adsrChangeSummary', projectId],
            });
            queryClient.invalidateQueries({
                queryKey: ['adsrUploadOutcome', projectId],
            });
            queryClient.invalidateQueries({ queryKey: ['adStatusRows', projectId] });
        },
    });
};

// POST .../ad-status-reports/pending/reject — keep the current report.
export const useRejectADSRUpdate = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.post(
                `/api/leasing/projects/${projectId}/ad-status-reports/pending/reject`,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['adsrChangeSummary', projectId],
            });
            queryClient.invalidateQueries({
                queryKey: ['adsrUploadOutcome', projectId],
            });
        },
    });
};

// DELETE /api/leasing/documents/{docId} — delete a surfaced report (e.g. a
// wrong-aircraft / msn_mismatch upload). The doc's version pointer is removed by
// the FK ON DELETE CASCADE, so the surfaced outcome clears on its own.
export const useDeleteADSRDocument = (projectId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (docId: number) => {
            await client.delete(`/api/leasing/documents/${docId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['adsrUploadOutcome', projectId],
            });
            queryClient.invalidateQueries({ queryKey: ['adStatusRows', projectId] });
        },
    });
};

// GET /api/leasing/ad-status-reports/{doc_id}/rows/{row_index}
export const useADStatusRow = (docId: number, rowIndex: number) => {
    const { client, enabled } = useAuthAxios();
    const isValid =
        Number.isFinite(docId) &&
        docId > 0 &&
        Number.isFinite(rowIndex) &&
        rowIndex >= 0;

    return useQuery({
        queryKey: ['adStatusRow', docId, rowIndex],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/ad-status-reports/${docId}/rows/${rowIndex}`,
            );
            return toCamelCase<ADStatusRowWithContext>(res.data);
        },
        enabled: enabled && isValid,
    });
};

// GET /api/leasing/projects/{project_id}/sb-status-rows
export const useSBStatusRows = (projectId: number) => {
    const { client, enabled } = useAuthAxios();
    const isValidProjectId = Number.isFinite(projectId) && projectId > 0;

    return useQuery({
        queryKey: ['sbStatusRows', projectId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/sb-status-rows`,
            );
            return toCamelCase<SBStatusReportResponse>(res.data);
        },
        enabled: enabled && isValidProjectId,
    });
};

type CitationsByProjectParams = {
    projectId: number;
    document?: LeasingDocs;
};

// GET /api/leasing/projects/{id}/documents
export const useCitationsByProject = ({
    projectId,
    document,
}: CitationsByProjectParams) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            'citationsByProject',
            projectId,
            document?.type,
            document?.canonicalId,
            document?.revision,
        ],
        queryFn: async () => {
            const docType = document?.type;
            const { canonicalId, revision } = document!;
            const res = await client.get(
                `/api/leasing/projects/${projectId}/citations`,
                {
                    params: toSnakeCase({ docType, canonicalId, revision }),
                },
            );
            return toCamelCase<LeasingDocs[]>(res.data);
        },
        enabled: enabled && !!document,
    });
};

// GET /api/leasing/{id}
export const useDocument = (id?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingDocument', id],
        queryFn: async () => {
            let res = await client.get(`/api/leasing/${id}`);
            return toCamelCase<LeasingDocs>(res.data);
        },
        enabled: !!id && enabled,
    });
};

export const useUpdateDocumentStatus = (id: number) => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            status,
            comment,
        }: {
            status: DocumentStatus;
            comment?: string;
        }) => {
            await client.post(
                `/api/leasing/${id}/status`,
                toSnakeCase({ status, comment }),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['leasingDocument', id],
            }),
    });
};

interface ListActivities {
    partNumber?: string;
    projectId: number;
    limit?: number;
    docIds?: number[];
}

// GET /api/leasing/{project_id}/activities
export const useActivities = ({
    projectId,
    partNumber,
    limit,
    docIds,
}: ListActivities) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingActivities', projectId, partNumber, docIds],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/${projectId}/activities`,
                {
                    params: toSnakeCase(
                        omitBy({ limit, partNumber, docIds }, isNil),
                    ),
                    paramsSerializer: { indexes: null },
                },
            );
            return toCamelCase<Activity[]>(res.data);
        },
        refetchInterval: 10_000,
        refetchOnMount: true,
        enabled,
    });
};

// GET /api/leasing/{project_id}/documents-by-section
export const listComponents = async (
    client: AxiosInstance,
    leasingProjectId: number,
    limit: number,
    skip: number,
    category?: 'H000' | 'H001' | 'H002',
    searchTerm?: string,
    sort?: ComponentSort,
) => {
    let res = await client.get(`/api/components/`, {
        params: toSnakeCase({
            leasingProjectId,
            limit,
            skip,
            category,
            searchTerm,
            sort,
        }),
    });
    return toCamelCase<PaginatedResponse<ComponentWithInstalledItem>>(res.data);
};

const getComponentById = async (
    client: AxiosInstance,
    componentId: number,
) => {
    const res = await client.get(`/api/components/${componentId}`);
    return toCamelCase<Component>(res.data);
};

const listComponentItems = async (
    client: AxiosInstance,
    componentId: number,
) => {
    const res = await client.get(`/api/components/${componentId}/items`);
    return toCamelCase<ComponentItem[]>(res.data);
};

// GET /api/components
export const useComponents = (
    leasingProjectId: number,
    limit: number,
    skip: number,
    category?: 'H000' | 'H001' | 'H002',
    searchTerm?: string,
    /** Sorted before paging, server-side; omitted keeps the part-number order. */
    sort?: ComponentSort,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            'componentsByProjectId',
            { leasingProjectId, limit, skip, category, searchTerm, sort },
        ],
        queryFn: () =>
            listComponents(
                client,
                leasingProjectId,
                limit,
                skip,
                category,
                searchTerm,
                sort,
            ),
        enabled,
        refetchInterval: 10_000,
        refetchOnMount: true,
    });
};

// GET /api/components/maintenance-activities
export const useComponentMaintenanceActivities = (
    leasingProjectId: number,
    limit: number,
    skip: number,
    category?: 'H000' | 'H001' | 'H002' | 'B013',
    searchTerm?: string,
    aiInsight?: StatusReportAIInsight,
    reportDocId?: number,
    reportTab?: string,
    isEnabled = true,
) => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: [
            'componentMaintenanceActivities',
            { leasingProjectId, limit, skip, category, searchTerm, aiInsight, reportDocId, reportTab },
        ],
        queryFn: async () => {
            const res = await client.get('/api/components/maintenance-activities', {
                params: toSnakeCase(
                    omitBy(
                        { leasingProjectId, limit, skip, category, searchTerm, aiInsight, reportDocId, reportTab },
                        isNil,
                    ),
                ),
            });
            return toCamelCase<PaginatedResponse<MaintenanceActivityRow>>(res.data);
        },
        enabled: enabled && !!leasingProjectId && isEnabled,
        // Only poll while it's working. This request is expensive on a large project, so
        // polling through a failure stacks copies on the database faster than they finish.
        // Focus refetch is off for the same reason; a remount is what retries it.
        refetchInterval: (query) =>
            query.state.status === 'error' ? false : 10_000,
        refetchOnWindowFocus: false,
        retry: 1,
        refetchOnMount: true,
    });
};

// GET /api/components/maintenance-activities/ata-chapters
export const useComponentAtaChapters = (
    leasingProjectId: number,
    category?: 'H000' | 'H001' | 'H002' | 'B013',
) => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['componentAtaChapters', { leasingProjectId, category }],
        queryFn: async () => {
            const res = await client.get(
                '/api/components/maintenance-activities/ata-chapters',
                {
                    params: toSnakeCase(
                        omitBy({ leasingProjectId, category }, isNil),
                    ),
                },
            );
            return toCamelCase<string[]>(res.data);
        },
        enabled: enabled && !!leasingProjectId,
        // No poll: the ATA-chapter set is effectively static; refetchOnMount is enough.
        refetchOnMount: true,
    });
};

// GET /api/components/report-summaries
export const useReportSummaries = (
    leasingProjectId: number,
    category?: 'H000' | 'H001' | 'H002' | 'B013',
) => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['reportSummaries', { leasingProjectId, category }],
        queryFn: async () => {
            const res = await client.get('/api/components/report-summaries', {
                params: toSnakeCase(
                    omitBy({ leasingProjectId, category }, isNil),
                ),
            });
            return toCamelCase<ReportSummary[]>(res.data);
        },
        enabled: enabled && !!leasingProjectId,
        refetchInterval: 10_000,
        refetchOnMount: true,
    });
};

// GET /api/components/{componentId}/items
export const useComponentItems = (componentId?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['componentItems', componentId],
        queryFn: () => listComponentItems(client, componentId!),
        enabled: enabled && !!componentId,
        refetchOnMount: true,
    });
};

// GET /api/components/{componentId}
export const useComponentById = (componentId?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['componentById', componentId],
        queryFn: () => getComponentById(client, componentId!),
        enabled: enabled && !!componentId,
        refetchOnMount: true,
    });
};

type ComponentGetByPartNumberArgs = { partNumber: string | null; projectId: number };

// GET /api/leasing/projects/{id}/components/{part_number}
export const useComponentByPartNumber = (
    { partNumber, projectId }: ComponentGetByPartNumberArgs,
    { enabled: isWanted = true }: { enabled?: boolean } = {},
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['componentByPartNumber', { projectId, partNumber }],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/components/${partNumber}`,
            );
            return toCamelCase<Component>(res.data);
        },
        enabled: enabled && isWanted,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

// GET /api/leasing/projects/{id}/components/{part_number}/docs
export const useComponentDocs = ({
    partNumber,
    projectId,
}: ComponentGetByPartNumberArgs) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['componentDocsByPartNumber', { projectId, partNumber }],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/components/${partNumber}/docs`,
            );
            return toCamelCase<ComponentDoc[]>(res.data);
        },
        enabled: enabled,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

export const useUpdateComponentStatus = (
    partNumber: string,
    projectId: number,
) => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            status,
            comment,
        }: {
            status: ComponentStatus;
            comment?: string;
        }) => {
            await client.post(
                `/api/leasing/projects/${projectId}/components/${partNumber}/status`,
                toSnakeCase({ status, comment }),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['leasingDocument', projectId, partNumber],
            }),
    });
};

type MaintenanceActivitiesArgs = { componentId?: number; docId?: number };

export const useMaintenanceActivities = ({
    docId,
    componentId,
}: MaintenanceActivitiesArgs) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['maintenanceActivities', docId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/documents/${docId}/maintenance_activities`,
                { params: toSnakeCase(omitBy({ componentId }, isNil)) },
            );
            return toCamelCase<MaintenanceTask[]>(res.data);
        },
        enabled: !!docId && !!componentId && enabled,
    });
};

export const useMaintenancePlan = ({ id }: { id?: number }) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['maintenancePlan', id],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/maintenance_plans/${id}`,
            );
            return toCamelCase<MaintenancePlan>(res.data);
        },
        enabled: !!id && enabled,
    });
};

export const useFileUploadProgress = (ingestedFileIds: number[]) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasing', 'upload_progress', { ingestedFileIds }],
        queryFn: async () => {
            const res = await client.get('/api/leasing/upload_progress', {
                params: toSnakeCase({ ingestedFileIds }),
                paramsSerializer: { indexes: null },
            });
            return toCamelCase<FileUploadProgress[]>(res.data);
        },
        refetchInterval: 3000,
        enabled: enabled && ingestedFileIds.length > 0,
    });
};

export const useUpdateADStatus = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            status,
            adId,
        }: {
            status: boolean;
            adId: number;
        }) => {
            await client.post(
                `/api/leasing/update_status_ad/${adId}`,
                toSnakeCase({ status }),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['updateAdStatus'],
            }),
    });
};

export const useUpdateWorkOrderCompletionDate = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            completionDate,
            docId,
        }: {
            completionDate: string;
            docId: number;
        }) => {
            await client.post(
                `/api/leasing/update_wo_completion_date/${docId}`,
                toSnakeCase({ completionDate }),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leasingDocument'],
            });
        },
    });
};

export const useComplianceDocumentCheckResults = (docId?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['complianceDocumentCheckResults', docId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/documents/${docId}/compliance_check_results`,
            );
            return toCamelCase<ComplianceCheckResult[]>(res.data);
        },
        enabled: !!docId && enabled,
    });
};

export const useComplianceComponentCheckResults = (componentId?: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['complianceComponentCheckResults', componentId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/components/${componentId}/compliance_check_results`,
            );
            return toCamelCase<ComplianceCheckResult[]>(res.data);
        },
        enabled: !!componentId && enabled,
    });
};

export const useComplianceProjectSummary = (projectId: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['complianceProjectSummary', projectId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/compliance_summary`,
            );
            return toCamelCase<CategoryComplianceStats[]>(res.data);
        },
        enabled,
    });
};

export const useProjectDocComplianceFailures = (projectId: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['projectDocComplianceFailures', projectId],
        queryFn: async () => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/documents/compliance_check_results`,
            );
            return toCamelCase<ProjectDocComplianceFailure[]>(res.data);
        },
        enabled: !!projectId && enabled,
    });
};

export const useComplianceLineItemCheckResults = ({
    lineItemId,
    isEnabled = true,
}: {
    lineItemId?: number;
    isEnabled?: boolean;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['line_items', lineItemId, 'compliance_check_results'],
        queryFn: async () => {
            const res = await client.get(
                `/api/line_items/${lineItemId}/compliance_check_results`,
            );
            return toCamelCase<ComplianceCheckResult[]>(res.data);
        },
        enabled: !!lineItemId && enabled && isEnabled,
    });
};

export const useComplianceJobCardsCheckResults = ({
    lineItemId,
    projectId,
    docType,
}: {
    lineItemId?: number;
    projectId?: number;
    docType: DocType.TASK_CARD | DocType.DISCREPANCY_REPORT;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            'line_items',
            lineItemId,
            'job_cards',
            'compliance_check_results',
            {
                projectId,
                docType,
            },
        ],
        queryFn: async () => {
            const res = await client.get(
                `/api/line_items/${lineItemId}/job_cards/compliance_check_results`,
                { params: { project_id: projectId, doc_type: docType } },
            );
            return toCamelCase<
                JobCardComplianceGroup[] | DiscrepancyReportComplianceGroup[]
            >(res.data);
        },
        enabled: !!lineItemId && !!projectId && enabled,
    });
};

export const useRerunComplianceChecks = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ lineItemId }: { lineItemId: number }) => {
            const response = await client.post(
                `/api/leasing/compliance_checks/rerun`,
                {
                    target_type: 'line_items',
                    target_id: lineItemId,
                },
            );
            return toCamelCase(response.data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['lineItems', variables.lineItemId],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'line_items',
                    variables.lineItemId,
                    'compliance_check_results',
                ],
            });
        },
    });
};

// GET /api/leasing/projects/{project_id}/ad-status-reports/summary_stats
export const useADStatusFolderSummaryStats = (projectId: number) => {
    const { client, enabled } = useAuthAxios();
    const isValid = Number.isFinite(projectId) && projectId > 0;

    return useQuery({
        queryKey: ['adStatusFolderSummaryStats', projectId],
        queryFn: async (): Promise<ADStatusReportSummaryStats> => {
            const res = await client.get(
                `/api/leasing/projects/${projectId}/ad-status-reports/summary_stats`,
            );
            return toCamelCase<ADStatusReportSummaryStats>(res.data);
        },
        enabled: enabled && isValid,
    });
};
